<template>
	<Card bordered size="large" :loading :content-class="total ? 'p-0!' : undefined" class="overflow-hidden">
		<template #header>
			<div class="flex items-start justify-between gap-4">
				<div class="flex flex-col gap-2">
					<div class="text-fs-base font-medium">{{ $t(`dashboard.pregnant_patients`) }}</div>
					<div v-if="total" class="text-fs-4xl font-bold">{{ total }}</div>
				</div>
			</div>
		</template>

		<table
			v-if="total"
			class="charts-css column show-labels show-primary-axis show-6-secondary-axes"
			style="--primary-axis-color: var(--grey-200-color)"
		>
			<tbody class="h-47.5 pt-10! pb-3.5!">
				<tr
					v-for="item in series"
					:key="item.label"
					class="px-2!"
					:class="{ 'pl-6!': item.isFirst, 'pr-6!': item.isLast }"
					style="
						background-image:
							repeating-linear-gradient(to right, white 0 2px, transparent 2px 6px),
							linear-gradient(
								var(--grey-100-color) var(--secondary-axes-width),
								transparent var(--secondary-axes-width)
							);

						background-size:
							12px calc(100% / 6),
							100% calc(100% / 6);

						background-repeat: repeat;
					"
				>
					<th scope="row" class="px-2!" :class="{ 'pl-6!': item.isFirst, 'pr-6!': item.isLast }">
						<div class="text-fs-sm -mb-1 max-w-full truncate font-normal">
							{{ item.label }}
						</div>
					</th>
					<td :style="{ '--size': item.size, '--color': item.color }" class="rounded-t-lg">
						<div
							class="text-fs-xs text-primary-700 border-primary-300 bg-primary-200 absolute -top-8 left-0 rounded-sm border px-1.5 py-1 font-medium"
						>
							{{ item.value }}
						</div>
					</td>
				</tr>
			</tbody>
		</table>

		<Card v-else embedded size="small" content-class="flex flex-col gap-4" class="min-h-full">
			<div class="bg-grey-100 flex size-12 items-center justify-center rounded-full">
				<Icon name="carbon:chart-stacked" :size="24" />
			</div>
			<div class="text-fs-md text-secondary">
				{{ $t(`dashboard.no_pregnant_patients_message`) }}
			</div>
		</Card>
	</Card>
</template>

<script lang="ts" setup>
import type { I18nString } from "@/lang/config"
import type { Trimesters } from "@/types/dashboard"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import Card from "@/components/common/Card.vue"
import Icon from "@/components/common/Icon.vue"
import "charts.css"

const props = defineProps<{
	loading?: boolean
	data?: Trimesters
}>()

const { t } = useI18n()

const total = computed(() => Object.values(props.data || {}).reduce((acc, curr) => acc + curr, 0) || 0)

const series = computed(() =>
	props.data
		? Object.entries(props.data).map(([key, val], index) => {
				const max = Math.max(...Object.values(props.data || {}))
				const size = val / max

				let color = "var(--primary-500-color)"
				if (key === "1") color = "var(--primary-500-color)"
				if (key === "2") color = "var(--primary-400-color)"
				if (key === "3") color = "var(--primary-300-color)"

				const isFirst = index === 0
				const isLast = index === Object.entries(props.data || {}).length - 1

				return {
					label: `${key}° ${t<I18nString>("common.misc.quarter")}`,
					value: val,
					color,
					size,
					isFirst,
					isLast
				}
			})
		: []
)
</script>
